import React, {useMemo, useState, useRef, useCallback, useEffect} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {
    Box,
    Grid,
    Stack,
    Typography,
    TextField,
    Card,
    CardActionArea,
    CardContent,
    Radio,
    Tooltip,
    Button,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions, Checkbox
} from "@mui/material";
import {
    HelpOutline,
} from "@mui/icons-material";
import "emoji-picker-element";
import {useDispatch, useSelector} from "react-redux";
import OTPMessageContentSection from '../OTPMessageContentSection'
import HeaderSection from "./components/TemplateHeader";
import ButtonsSection from "./components/TemplateButtons";
import FooterSection from "./components/TemplateFooter";
import BodySection from "./components/TemplateBody";
import PhonePreview from "./components/TemplatePhonePreview";
import PreviewModal from "./components/TemplatePreviewModal";
import CarouselSection from "./components/TemplateCarouselForm";
import mapCardsForPreview from "./utils/mapCardsForPreview";
import DynamicButtonFields from "./utils/DynamicButtonFields";
import {useTemplateTypeOptions} from "./utils/useTemplateTypeOptions";
import {useBuildPayload} from "./hooks/useBuildPayload";
import {createTemplate} from "../../../actions/templates";
import {getBotSteps} from "../../../actions/botActions";
import {useMsal} from "@azure/msal-react";
import {useToken} from "../../../hooks/useToken";


/**
 * TemplateCreator — Headers por tipo + Body con variables (máx 10) +
 * Quick Replies (máx 10) + CTA (máx 2 URL, 1 Phone)
 */
export default function TemplateCreator({ onSuccess }) {
    const {name, gupshup_app_id, id_customer} = useSelector((state) => state.agent);
    const {instance} = useMsal();
    const {tokenRequest} = useToken();

    // --- Form state ---
    const [templateName, setTemplateName] = useState("");
    const [category, setCategory] = useState("utility");
    const [language, setLanguage] = useState("es");
    const [labels, setLabels] = useState("");

    // Header
    const [headerType, setHeaderType] = useState("none");
    const [headerText, setHeaderText] = useState("");
    const [headerTextVariable, setHeaderTextVariable] = useState("");
    const [headerMedia, setHeaderMedia] = useState(null);

    const [headerMediaError, setHeaderMediaError] = useState("");

    // Location
    const [lat, setLat] = useState("");
    const [lng, setLng] = useState("");
    const [locName, setLocName] = useState("");
    const [locAddress, setLocAddress] = useState("");

    // Body/Footer
    const [body, setBody] = useState("");
    const [footer, setFooter] = useState("");
    const [bodySamples, setBodySamples] = useState({}); // { '1': 'Juan', ... }

    // Buttons
    const [quickReplies, setQuickReplies] = useState([]); // strings
    const [ctas, setCtas] = useState([]); // {type:'url'|'phone', title:'', urlType?, value:'', sampleVar?}
    const [ctaMenuEl, setCtaMenuEl] = useState(null);

    // Constraints
    const HEADER_LIMIT = 60;
    const BODY_LIMIT = 550;
    const FOOTER_LIMIT = 60;
    const MAX_BUTTONS = 10; // total (quick + ctas)
    const MAX_BODY_VARS = 10;
    const MAX_URL_CTAS = 2;
    const MAX_PHONE_CTAS = 1;

    const remainingFooter = FOOTER_LIMIT - footer.length;
    const totalButtons = quickReplies.length + ctas.length;
    const urlCount = ctas.filter((c) => c.type === "url").length;
    const phoneCount = ctas.filter((c) => c.type === "phone").length;

    const [openPreview, setOpenPreview] = useState(false);
    const openPreviewModal = () => {
        setSubmitError("");
        setOpenPreview(true);
    };
    const closePreviewModal = () => setOpenPreview(false);
    const [submitting, setSubmitting] = useState(false);

    // AUTH-only extra options
    const [addSecurityRecommendation, setAddSecurityRecommendation] = useState(false);
    const [addExpirationTime, setAddExpirationTime] = useState(false);
    const [codeExpirationMinutes, setCodeExpirationMinutes] = useState("10");
    const navigate = useNavigate();
    const location = useLocation();
    const duplicatedTemplate = location.state?.duplicatedTemplate;

    const [openConfirm, setOpenConfirm] = useState(false);
    const [clearAllOpen, setClearAllOpen] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [templateType, setTemplateType] = useState(null);
    // Estado para el formato de Product Message
    const [productFormat, setProductFormat] = useState("multi");
    const [ltoTitle, setLtoTitle] = useState("");
    const [ltoHasExpiry, setLtoHasExpiry] = useState(false);
    const [initStep, setInitStep] = useState("");
    const dispatch = useDispatch();
    const botSteps = useSelector((state) => state.bot?.bot_steps || []);

    // Estados para el Carousel
    const [carouselCards, setCarouselCards] = useState([
        {
            id: 1,
            mediaFile: null,
            description: "",
            buttonType: "quick_reply",
            buttonTitle: "",
            buttonUrlType: "", // <- solo se usa para URL
            buttonValue: "",
            variables: [],
        }

    ]);
    const [headerMediaType, setHeaderMediaType] = useState("image");
    const [numButtons, setNumButtons] = useState(1);
    // Estado de tipos globales
    const [buttonTypes, setButtonTypes] = useState(["quick_reply"]);
    const templateTypeOptions = useTemplateTypeOptions(category);
// Cada vez que cambia numButtons, ajustamos el array manteniendo los existentes
    useEffect(() => {
        setButtonTypes((prev) => {
            const next = [...prev];
            if (numButtons > prev.length) {
                // Añadir nuevos botones con valor por defecto
                for (let i = prev.length; i < numButtons; i++) {
                    next.push("quick_reply");
                }
            } else if (numButtons < prev.length) {
                // Eliminar los botones que sobran
                next.splice(numButtons);
            }
            return next;
        });
    }, [numButtons]);

    // Cambiar tipo de botón individual
    const handleButtonTypeChange = (index, value) => {
        setButtonTypes((prev) =>
            prev.map((t, i) => (i === index ? value : t))
        );
    };

    // Cada vez que cambie la categoría, selecciona la primera opción del nuevo grupo
    useEffect(() => {
        if (templateTypeOptions.length > 0 && templateType == null) {
            setTemplateType((prev) => {
                const stillValid = templateTypeOptions.some((opt) => opt.value === prev);
                return stillValid ? prev : templateTypeOptions[0].value;
            });
        }
    }, [category, templateTypeOptions, templateType]);
    const handleAddCard = () => {
        setCarouselCards((prev) => [
            ...prev,
            {
                id: prev.length + 1,
                mediaFile: null,
                description: "",
                buttonType: "quick_reply",
                buttonTitle: "",
                variables: [],
            },
        ]);
    };

    const handleRemoveCard = (id) => {
        setCarouselCards((prev) => prev.filter((c) => c.id !== id));
    };

    const handleCardChange = (id, field, value) => {
        setCarouselCards(prev =>
            prev.map(c => (c.id === id ? {...c, [field]: value} : c))
        );
    };


    const handleFileUpload = (id, file) => {
        setCarouselCards((prev) =>
            prev.map((c) => {
                if (c.id !== id) return c;
                // Revocar blob URL anterior si existe
                if (c.mediaPreview) URL.revokeObjectURL(c.mediaPreview);
                const mediaPreview = file ? URL.createObjectURL(file) : null;
                return {...c, mediaFile: file, mediaPreview};
            })
        );
    };

    const addVariableToCard = (id) => {
        setCarouselCards((prev) =>
            prev.map((c) =>
                c.id === id
                    ? {
                        ...c,
                        variables: [
                            ...c.variables,
                            `{{${c.variables.length + 1}}}`,
                        ],
                    }
                    : c
            )
        );
    };


    const headerTypeToTemplateType = {
        none: "TEXT",
        text: "TEXT",
        image: "IMAGE",
        video: "VIDEO",
        document: "DOCUMENT",
        location: "LOCATION",
    };
    const bodyPreview = useMemo(
        () =>
            body.replace(/\{\{(\d+)\}\}/g, (_, n) =>
                bodySamples?.[n] ? String(bodySamples[n]) : `{{${n}}}`
            ),
        [body, bodySamples]
    );

    const buildButtons = () => {
        if (category.toUpperCase() === "AUTHENTICATION") {
            return JSON.stringify([{type: "OTP", otp_type: "COPY_CODE"}])
        }

        const quick = quickReplies
            .filter((q) => q.trim() !== "")
            .map((q) => ({type: "QUICK_REPLY", text: q}));

        const cta = ctas.map((c) => {
            if (c.type === "phone") {
                return {type: "PHONE_NUMBER", text: c.title || "Call", phone_number: c.value};
            }
            if (c.urlType === "dynamic") {
                const hasQuery = c.value.includes("?");
                const hasTrailingSlash = c.value.endsWith("/");
                const sep = hasQuery || hasTrailingSlash ? "" : "/";
                const url = c.value + sep + "{{1}}";
                if (c.sampleVar?.trim()) {
                    const sample = c.sampleVar.trim();
                    const cleanSample = sep === "/" && sample.startsWith("/") ? sample.slice(1) : sample;
                    return {type: "URL", text: c.title || "Open link", url, example: [c.value + sep + cleanSample]};
                }
                return {type: "URL", text: c.title || "Open link", url};
            }
            const base = {type: "URL", text: c.title || "Open link", url: c.value};
            return base;
        });

        return JSON.stringify([...quick, ...cta]);
    };

    // Si usas variable de header en preview:
    const headerTextResolved =
        headerType === "text"
            ? (headerTextVariable ? headerText.replace("{{1}}", headerTextVariable) : headerText)
            : "";
    // 1) Agrega este handler (por ejemplo, debajo de handleSubmit)
    const handleClearAll = () => {
        // cerrar modales / menús
        setOpenPreview(false);
        setCtaMenuEl(null);
        setEmojiAnchor(null);

        // nombre / categoría / idioma / labels
        setTemplateName("");
        setCategory("utility");
        setLanguage("es");
        setLabels("");

        // header
        setHeaderType("none");
        setHeaderText("");
        setHeaderTextVariable("");

        // media + errores (+ liberar URL de preview si existiese)
        setHeaderMedia(null);
        if (headerMediaPreview) {
            URL.revokeObjectURL(headerMediaPreview);
        }
        setHeaderMediaPreview(null);
        setHeaderMediaError("");

        // ubicación
        setLat("");
        setLng("");
        setLocName("");
        setLocAddress("");

        // body / footer
        setBody("");
        setFooter("");
        setBodySamples({});

        // botones
        setQuickReplies([]);
        setCtas([]);

        // AUTH-only options
        setAddSecurityRecommendation(false);
        setAddExpirationTime(false);
        setCodeExpirationMinutes("10");

        // flags
        setSubmitting(false);
        setInitStep("");
        // Revocar blob URLs de carousel
        carouselCards.forEach((c) => {
            if (c.mediaPreview) URL.revokeObjectURL(c.mediaPreview);
        });
    };

    // Convierte File -> "data:<mime>;name=<encodedName>;base64,<b64>"
    const fileToExampleMediaString = (file) =>
        new Promise((resolve, reject) => {
            // 🔒 Evita errores si file es null o no es File/Blob
            if (!file || !(file instanceof Blob)) {
                return resolve("");
            }

            const reader = new FileReader();
            reader.onload = () => {
                const result = String(reader.result || "");
                const commaIdx = result.indexOf(",");
                if (commaIdx === -1) return resolve("");
                const mime = file.type || "application/octet-stream";
                const name = encodeURIComponent(file.name || "upload");
                const base64 = result.slice(commaIdx + 1);
                resolve(`data:${mime};name=${name};base64,${base64}`);
            };
            reader.onerror = (err) => reject(err);

            reader.readAsDataURL(file); // ✅ se ejecuta solo si file es válido
        });


    // ---- arma payload según headerType ----
    const {buildPayload} = useBuildPayload({
        category,
        templateName,
        language,
        body,
        bodyPreview,
        footer,
        headerType,
        headerText,
        headerTextVariable,
        headerMedia,
        labels,
        addSecurityRecommendation,
        addExpirationTime,
        codeExpirationMinutes,
        gupshup_app_id,
        headerTextResolved,
        headerTypeToTemplateType,
        fileToExampleMediaString,
        buildButtons,
        lat,
        lng,
        locName,
        locAddress,
        carouselCards,
        buttonTypes,
        templateType,
    });


    // ---- submit final ----
    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            const payload = await buildPayload();
            payload.init_step = initStep;
            console.log("payload", payload)
            const result = await dispatch(
                createTemplate({
                    payload,
                    username: name,
                    apiKey: gupshup_app_id,
                })
            );
            if (result.success) {
                setSubmitError("");
                setOpenPreview(false);
                setOpenConfirm(true);
            } else {
                let errorMsg = result.error;
                try {
                    const parsed = JSON.parse(result.error);
                    errorMsg = parsed.detail || parsed.error || result.error;
                } catch {}
                setSubmitError(errorMsg);
            }
        } catch (err) {
            console.error("❌ Error enviando template:", err);
        } finally {
            setSubmitting(false);
        }
    };

    // Header enabled
    const headerEnabled =
        headerType === "text" ? headerText.trim().length > 0 : headerType !== "none";

    // --- Media rules ---
    const MEDIA_LIMITS = {
        image: {exts: ["image/png", "image/jpeg"], maxBytes: 5 * 1024 * 1024, hint: "PNG/JPEG (≤ 5 MB)"},
        video: {exts: ["video/mp4"], maxBytes: 16 * 1024 * 1024, hint: "MP4 (≤ 16 MB)"},
        document: {exts: ["application/pdf"], maxBytes: 100 * 1024 * 1024, hint: "PDF (≤ 100 MB)"},
    };
    const validateHeaderMedia = (file, type) => {
        const rule = MEDIA_LIMITS[type];
        if (!rule) return "";
        if (!rule.exts.includes(file.type)) return `Formato no permitido. Usa ${rule.hint}.`;
        if (file.size > rule.maxBytes) return `El archivo excede el límite. Usa ${rule.hint}.`;
        return "";
    };
    const [headerMediaPreview, setHeaderMediaPreview] = useState(null);

    const handleFileChange = (e) => {
        setHeaderMediaError("");
        if (e.target.files && e.target.files[0]) {
            const f = e.target.files[0];
            const err = validateHeaderMedia(f, headerType);

            if (err) {
                setHeaderMedia(null);
                setHeaderMediaPreview(null);
                setHeaderMediaError(err);
            } else {
                setHeaderMedia(f);

                // 👇 Crear preview temporal
                const objectUrl = URL.createObjectURL(f);
                setHeaderMediaPreview(objectUrl);
            }
        }
    };


    // --- Body helpers (variables) ---
    const bodyVarIndices = useMemo(() => {
        const ids = new Set();
        const re = /\{\{(\d+)\}\}/g;
        let m;
        while ((m = re.exec(body)) !== null) ids.add(m[1]);
        return Array.from(ids).sort((a, b) => Number(a) - Number(b));
    }, [body]);

    const handleAddBodyVariable = () => {
        if (bodyVarIndices.length >= MAX_BODY_VARS) return;
        const next =
            bodyVarIndices.length > 0
                ? Number(bodyVarIndices[bodyVarIndices.length - 1]) + 1
                : 1;
        setBody((b) => b + (b && !b.endsWith(" ") ? " " : "") + `{{${next}}}`);
    };

    // Derivados para el modal
    const headerTypeLabel = (headerType === "none" ? "NONE" : headerType.toUpperCase());

    // --- Body helpers (toolbar/emoji) ---
    const [emojiAnchor, setEmojiAnchor] = useState(null);
    const bodyInputRef = useRef(null);

    // nodo actual del web component
    const emojiNodeRef = useRef(null);

    const insertText = useCallback(
        (snippet) => {
            const el = bodyInputRef.current;
            const start = el?.selectionStart ?? body.length;
            const end = el?.selectionEnd ?? body.length;
            const next = body.slice(0, start) + snippet + body.slice(end);
            setBody(next.slice(0, BODY_LIMIT));
            requestAnimationFrame(() => {
                if (!el) return;
                const pos = Math.min(start + snippet.length, BODY_LIMIT);
                el.setSelectionRange(pos, pos);
                el.focus();
            });
        },
        [body]
    );

    // handler estable para el evento del picker
    const onEmojiPick = useCallback(
        (e) => {
            const d = e?.detail ?? {};
            const unicode =
                d.unicode ||
                d.emoji?.unicode ||
                d.emoji?.native ||
                d.emoji?.unicodeSkintone;
            if (unicode) {
                insertText(unicode);
                setEmojiAnchor(null);
            }
        },
        [insertText]
    );

    // callback ref que adjunta / limpia listener en el nodo correcto
    const setEmojiPickerRef = useCallback(
        (node) => {
            if (emojiNodeRef.current) {
                emojiNodeRef.current.removeEventListener("emoji-click", onEmojiPick);
            }
            if (node) {
                node.addEventListener("emoji-click", onEmojiPick);
            }
            emojiNodeRef.current = node;
        },
        [onEmojiPick]
    );

    // limpieza al desmontar
    useEffect(() => {
        return () => {
            if (emojiNodeRef.current) {
                emojiNodeRef.current.removeEventListener("emoji-click", onEmojiPick);
            }
        };
    }, [onEmojiPick]);
    useEffect(() => {
        return () => {
            if (headerMediaPreview) {
                URL.revokeObjectURL(headerMediaPreview);
            }
        };
    }, [headerMediaPreview]);
    const carouselUrlsRef = useRef([]);
    useEffect(() => {
        carouselUrlsRef.current = carouselCards
            .map((c) => c.mediaPreview)
            .filter(Boolean);
    }, [carouselCards]);
    useEffect(() => {
        return () => {
            carouselUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
        };
    }, []);

    // Cargar datos del template duplicado (solo al montar)
    useEffect(() => {
        if (!duplicatedTemplate) return;

        console.log('[TemplateForm] duplicatedTemplate:', JSON.parse(JSON.stringify(duplicatedTemplate)));

        // Nombre con sufijo _copy
        const copyName = duplicatedTemplate.name
            ? `${duplicatedTemplate.name}_copy`.slice(0, 512)
            : '';
        setTemplateName(copyName);

        // Categoría
        const cat = duplicatedTemplate.category?.toLowerCase();
        if (['marketing', 'utility', 'authentication'].includes(cat)) {
            setCategory(cat);
        }

        // Language
        if (duplicatedTemplate.language) {
            setLanguage(duplicatedTemplate.language);
        }

        // Labels
        if (duplicatedTemplate.labels) {
            setLabels(duplicatedTemplate.labels);
        }

        // Template Type — API usa `type`, no `templateType`
        if (duplicatedTemplate.type) {
            const TYPE_TO_TEMPLATE_TYPE = {
                TEXT: 'custom_message',
                IMAGE: 'custom_message',
                VIDEO: 'custom_message',
                DOCUMENT: 'custom_message',
                CAROUSEL: 'carousel',
            };
            const mapped = TYPE_TO_TEMPLATE_TYPE[duplicatedTemplate.type.toUpperCase()];
            if (mapped) setTemplateType(mapped);
        }

        // Componentes
        const components = duplicatedTemplate.components || [];
        const headerComp = components.find(c => c.type === 'header');
        const bodyComp = components.find(c => c.type === 'body');
        const footerComp = components.find(c => c.type === 'footer');
        const carouselComp = components.find(c => c.type === 'carousel');

        // Header — first try from components, fallback to top-level type
        const setHeaderFromType = (typeUpper) => {
            if (typeUpper === 'TEXT') {
                setHeaderType('text');
                setHeaderText(headerComp?.text || '');
            } else if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(typeUpper)) {
                setHeaderType(typeUpper.toLowerCase());
                if (duplicatedTemplate.url) {
                    setHeaderMediaPreview(duplicatedTemplate.url);
                    fetch(duplicatedTemplate.url)
                        .then(res => {
                            if (!res.ok) throw new Error('fetch failed');
                            return res.blob();
                        })
                        .then(blob => {
                            const urlParts = duplicatedTemplate.url.split('/');
                            const fileName = urlParts[urlParts.length - 1] || `header.${blob.type.split('/')[1] || 'bin'}`;
                            const file = new File([blob], fileName, { type: blob.type });
                            setHeaderMedia(file);
                        })
                        .catch(() => {});
                }
            } else {
                setHeaderType('none');
            }
        };

        if (headerComp) {
            const format = (headerComp.format || '').toUpperCase();
            if (['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'].includes(format)) {
                setHeaderFromType(format);
            } else if (duplicatedTemplate.type) {
                // headerComp exists but uses parameters structure (no format field) — fallback to top-level type
                setHeaderFromType(duplicatedTemplate.type.toUpperCase());
            } else {
                setHeaderType('none');
            }
        } else if (duplicatedTemplate.type) {
            // No header component at all — infer from top-level type
            setHeaderFromType(duplicatedTemplate.type.toUpperCase());
        }

        // Body
        setBody(bodyComp?.text || duplicatedTemplate.body || '');

        // Footer — API usa parameters[{text}]
        if (footerComp) {
            setFooter(footerComp.parameters?.map(p => p.text || '').filter(Boolean).join('') || '');
        }

        // Buttons — API devuelve múltiples componentes type="button", uno por botón
        const buttonComps = components.filter(c => c.type === 'button');
        if (buttonComps.length > 0) {
            const qrs = [];
            const ctaList = [];
            const types = [];
            buttonComps.forEach((btn) => {
                const subType = (btn.sub_type || '').toUpperCase();
                const btnParams = btn.parameters?.[0] || {};
                if (subType === 'QUICK_REPLY') {
                    qrs.push(btnParams.payload || '');
                    types.push('quick_reply');
                } else if (subType === 'URL') {
                    ctaList.push({
                        type: 'url',
                        title: btnParams.text || '',
                        value: btnParams.url || '',
                        urlType: btnParams.example ? 'dynamic' : 'static',
                        sampleVar: btnParams.example ? btnParams.example[0] : '',
                    });
                    types.push('cta');
                } else if (subType === 'PHONE_NUMBER') {
                    ctaList.push({
                        type: 'phone',
                        title: btnParams.text || '',
                        value: btnParams.phone_number || '',
                    });
                    types.push('cta');
                }
            });
            setQuickReplies(qrs);
            setCtas(ctaList);
            if (types.length > 0) {
                setButtonTypes(types);
                setNumButtons(types.length);
            }
        }

        // Carousel
        if (carouselComp && carouselComp.cards) {
            const mappedCards = carouselComp.cards.map((card, idx) => {
                const cardComponents = card.components || [];
                const headerComp = cardComponents.find(c => c.type === 'header');
                const bodyComp = cardComponents.find(c => c.type === 'body');
                const btnComp = cardComponents.find(c => c.type === 'button');

                // Image URL from header parameters
                const imageLink = headerComp?.parameters?.[0]?.image?.link || '';

                // Body text from parameters
                const bodyText = bodyComp?.parameters
                    ?.map(p => p.text || '')
                    .filter(Boolean)
                    .join('') || '';

                // Button info
                let buttonType = 'quick_reply';
                let buttonTitle = '';
                let buttonValue = '';
                let buttonUrlType = '';

                if (btnComp) {
                    const subType = (btnComp.sub_type || '').toLowerCase();
                    const btnParams = btnComp.parameters?.[0] || {};
                    if (subType === 'url') {
                        buttonType = 'cta';
                        buttonTitle = btnParams.text || '';
                        buttonValue = btnParams.url || '';
                        buttonUrlType = btnParams.example ? 'dynamic' : '';
                    } else if (subType === 'phone_number') {
                        buttonType = 'cta';
                        buttonTitle = btnParams.text || '';
                        buttonValue = btnParams.phone_number || '';
                    } else {
                        // quick_reply
                        buttonType = 'quick_reply';
                        buttonTitle = btnParams.payload || '';
                    }
                }

                return {
                    id: idx + 1,
                    mediaFile: null,
                    mediaPreview: imageLink || undefined,
                    description: bodyText || '',
                    buttonType,
                    buttonTitle,
                    buttonUrlType,
                    buttonValue,
                    variables: [],
                };
            });
            setCarouselCards(mappedCards);
        }

        // AUTH options
        if (cat === 'authentication') {
            setAddSecurityRecommendation(true);
            setAddExpirationTime(true);
            if (duplicatedTemplate.codeExpirationMinutes) {
                setCodeExpirationMinutes(String(duplicatedTemplate.codeExpirationMinutes));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch bot steps
    useEffect(() => {
        if (!id_customer) return;
        instance.acquireTokenSilent(tokenRequest).then(({accessToken}) => {
            dispatch(getBotSteps(id_customer, accessToken));
        }).catch(() => {});
    }, [id_customer, instance, tokenRequest, dispatch]);

    const wrapSelection = (prefix, suffix = prefix) => {
        const el = bodyInputRef.current;
        if (!el) return;
        const start = el.selectionStart ?? 0;
        const end = el.selectionEnd ?? 0;
        const selected = body.slice(start, end) || "texto";
        const wrapped = `${prefix}${selected}${suffix}`;
        const next = body.slice(0, start) + wrapped + body.slice(end);
        setBody(next.slice(0, BODY_LIMIT));
        requestAnimationFrame(() => {
            const from = Math.min(start + prefix.length, BODY_LIMIT);
            const to = Math.min(from + selected.length, BODY_LIMIT);
            el.setSelectionRange(from, to);
            el.focus();
        });
    };

    const applyList = (ordered) => {
        const el = bodyInputRef.current;
        if (!el) return;
        const start = el.selectionStart ?? 0;
        const end = el.selectionEnd ?? 0;

        const before = body.lastIndexOf("\n", start - 1) + 1;
        const after = body.indexOf("\n", end);
        const selStart = before < 0 ? 0 : before;
        const selEnd = after === -1 ? body.length : after;

        const block = body.slice(selStart, selEnd);
        const lines = block.split("\n");
        const out = lines
            .map((ln, i) =>
                ordered
                    ? `${i + 1}. ${ln.replace(/^\d+\.\s|^- \s?/, "")}`
                    : `- ${ln.replace(/^\d+\.\s|^- \s?/, "")}`
            )
            .join("\n");

        const next = body.slice(0, selStart) + out + body.slice(selEnd);
        setBody(next.slice(0, BODY_LIMIT));
        requestAnimationFrame(() => {
            el.setSelectionRange(selStart, selStart + out.length);
            el.focus();
        });
    };
    // --- CTA helpers ---
    const openCtaMenu = (e) => setCtaMenuEl(e.currentTarget);
    const closeCtaMenu = () => setCtaMenuEl(null);

    const urlIsValid = (s) => /^https?:\/\//i.test(s?.trim() || "");
    const phoneIsValid = (s) => /^\+?[0-9\s\-()]{6,}$/.test(s?.trim() || "");

    // Validaciones generales
    const nameError = templateName.trim().length === 0;
    const bodyError = body.trim().length === 0 || body.length > BODY_LIMIT;
    const locationError =
        headerType === "location" &&
        (!lat || !lng || Number.isNaN(Number(lat)) || Number.isNaN(Number(lng)));

    const categoryCopy = useMemo(
        () => ({
            marketing: {
                title: "Marketing",
                desc: "Envía ofertas promocionales y más para aumentar el alcance y la interacción.",
                icon: "🛍️",
            },
            utility: {
                title: "Utilidad",
                desc: "Envía actualizaciones de cuenta, pedidos, alertas y más para compartir información importante.",
                icon: "🔔",
            },
            authentication: {
                title: "Autenticación",
                desc: "Envía códigos que permiten a tus clientes acceder a sus cuentas.",
                icon: "🔐",
            },
        }),
        []
    );

    // --- Render ---
    return (
        <Box component="form" noValidate sx={{p: {xs: 2, md: 3}}}>
            <Grid container spacing={3}>
                {/* LEFT: Form */}
                <Grid item xs={12} md={7}>
                    <Stack spacing={3}>

                        {/* Template name */}
                        <Paper variant="outlined" sx={{p: 2, borderRadius: 3, borderLeft: 3, borderLeftColor: "primary.main"}}>
                            <Stack gap={1}>
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <Typography variant="h6">Nombre del template</Typography>
                                    <Tooltip
                                        title="Solo minúsculas, números y guiones bajos.">
                                        <HelpOutline fontSize="small"/>
                                    </Tooltip>
                                </Stack>
                                <TextField
                                    placeholder="template_name"
                                    value={templateName}
                                    onChange={(e) =>
                                        setTemplateName(e.target.value.toLowerCase().replace(/\s+/g, "_"))
                                    }
                                    fullWidth
                                    required
                                    size="small"
                                    error={nameError}
                                    helperText={nameError ? "Requerido. Solo minúsculas, números y guiones bajos." : ""}
                                />
                            </Stack>
                        </Paper>

                        {/* Category */}
                        <Paper variant="outlined" sx={{p: 2, borderRadius: 3, borderLeft: 3, borderLeftColor: "primary.main"}}>
                            <Stack gap={2}>
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <Typography variant="h6">Categoría</Typography>
                                    <Tooltip title="Selecciona el tipo de plantilla a crear.">
                                        <HelpOutline fontSize="small"/>
                                    </Tooltip>
                                </Stack>
                                <Grid container spacing={2} sx={{ pb: 2 }}>
                                    {Object.entries(categoryCopy).map(([key, cfg]) => (
                                        <Grid item xs={12} sm={4} key={key}>
                                            <Card
                                                  variant={category === key ? "elevation" : "outlined"}
                                                  sx={{
                                                      borderRadius: 3,
                                                      height: "100%",
                                                      border: category === key ? 2 : undefined,
                                                      borderColor: category === key ? "primary.main" : undefined,
                                                      bgcolor: category === key ? "action.selected" : undefined,
                                                  }}>
                                                <CardActionArea onClick={() => setCategory(key)} sx={{ height: "100%", alignItems: "flex-start", display: "flex" }}>
                                                    <CardContent>
                                                        <Stack direction="row" alignItems="center" gap={1}>
                                                            <Typography fontWeight={700} sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                                                                <span>{cfg.icon}</span>
                                                                {cfg.title}
                                                            </Typography>
                                                        </Stack>
                                                        <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                                                            {cfg.desc}
                                                        </Typography>
                                                    </CardContent>
                                                </CardActionArea>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            </Stack>
                        </Paper>

                        {/* Template Type */}
                        {templateTypeOptions.length > 0 && (
                            <Paper variant="outlined" sx={{p: 2, borderRadius: 3, borderLeft: 3, borderLeftColor: "primary.main"}}>
                                <Stack gap={2}>
                                    <Stack direction="row" alignItems="center" gap={1}>
                                        <Typography variant="h6">Tipo de plantilla</Typography>
                                        <Tooltip title="Selecciona el tipo de plantilla a crear.">
                                            <HelpOutline fontSize="small"/>
                                        </Tooltip>
                                    </Stack>

                                    <FormControl fullWidth>
                                        <InputLabel id="template-type-label">Seleccionar tipo</InputLabel>
                                        <Select
                                            labelId="template-type-label"
                                            label="Seleccionar tipo"
                                            value={templateType || ''}
                                            onChange={(e) => setTemplateType(e.target.value)}
                                            renderValue={(value) => {
                                                const opt = templateTypeOptions.find((o) => o.value === value);
                                                return opt?.label || "";
                                            }}
                                        >
                                            {templateTypeOptions.map((opt) => (
                                                <MenuItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                                                    <Box>
                                                        <Typography fontWeight={600}>{opt.label}</Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {opt.desc}
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>
                            </Paper>
                        )}

                        {/* Product Message Format Section */}
                        {templateType === "product_message" && (
                            <Paper variant="outlined" sx={{p: 2, borderRadius: 3, borderLeft: 3, borderLeftColor: "primary.main"}}>
                                <Stack gap={2}>
                                    <Stack direction="row" alignItems="center" gap={1}>
                                        <Typography variant="h6">Formato de plantilla</Typography>
                                        <Tooltip title="Choose what type of message template you want to create.">
                                            <HelpOutline fontSize="small"/>
                                        </Tooltip>
                                    </Stack>

                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Card
                                                variant={productFormat === "catalogue" ? "elevation" : "outlined"}
                                                sx={{
                                                    borderRadius: 3,
                                                    borderColor: productFormat === "catalogue" ? "primary.main" : "divider",
                                                    transition: "0.2s",
                                                    "&:hover": {transform: "scale(1.02)"},
                                                }}
                                            >
                                                <CardActionArea onClick={() => setProductFormat("catalogue")}>
                                                    <CardContent>
                                                        <Stack direction="row" alignItems="center" gap={1}>
                                                            <Radio checked={productFormat === "catalogue"}
                                                                   value="catalogue"/>
                                                            <Typography fontWeight={700}>Catalogue Message</Typography>
                                                        </Stack>
                                                        <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                                                            Include the entire catalogue to give your users a
                                                            comprehensive view of all your products.
                                                        </Typography>
                                                    </CardContent>
                                                </CardActionArea>
                                            </Card>
                                        </Grid>

                                        <Grid item xs={12} sm={6}>
                                            <Card
                                                variant={productFormat === "multi" ? "elevation" : "outlined"}
                                                sx={{
                                                    borderRadius: 3,
                                                    borderColor: productFormat === "multi" ? "primary.main" : "divider",
                                                    transition: "0.2s",
                                                    "&:hover": {transform: "scale(1.02)"},
                                                }}
                                            >
                                                <CardActionArea onClick={() => setProductFormat("multi")}>
                                                    <CardContent>
                                                        <Stack direction="row" alignItems="center" gap={1}>
                                                            <Radio checked={productFormat === "multi"} value="multi"/>
                                                            <Typography fontWeight={700}>Multi-Product
                                                                Message</Typography>
                                                        </Stack>
                                                        <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                                                            Include up to 30 products from the catalogue. Useful for
                                                            showcasing a new collection or a specific
                                                            product category.
                                                        </Typography>
                                                    </CardContent>
                                                </CardActionArea>
                                            </Card>
                                        </Grid>
                                    </Grid>
                                </Stack>
                            </Paper>
                        )}

                        {/* Language */}
                        <Paper variant="outlined" sx={{p: 2, borderRadius: 3, borderLeft: 3, borderLeftColor: "primary.main"}}>
                            <Stack gap={2}>
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <Typography variant="h6">Idioma</Typography>
                                    <Tooltip title="Selecciona el idioma de la plantilla.">
                                        <HelpOutline fontSize="small"/>
                                    </Tooltip>
                                </Stack>
                                <FormControl fullWidth>
                                    <InputLabel id="lang-label">Seleccionar</InputLabel>
                                    <Select
                                        labelId="lang-label"
                                        label="Seleccionar"
                                        value={language}
                                        onChange={(e) => setLanguage(e.target.value)}
                                    >
                                        <MenuItem value="es">Spanish (es)</MenuItem>
                                        <MenuItem value="en">English (en)</MenuItem>
                                        <MenuItem value="pt">Portuguese (pt)</MenuItem>
                                    </Select>
                                </FormControl>
                            </Stack>
                        </Paper>

                        {/* Template Labels */}
                        <Paper variant="outlined" sx={{p: 2, borderRadius: 3, borderLeft: 3, borderLeftColor: "primary.main"}}>
                            <Stack gap={1}>
                                <Typography variant="h6">Etiquetas</Typography>
                                <TextField
                                    placeholder="Ej: Actualización de cuenta, OTP"
                                    fullWidth
                                    size="small"
                                    value={labels}
                                    onChange={(e) => setLabels(e.target.value)}
                                />
                            </Stack>
                        </Paper>

                        {/* Init Step */}
                        <Paper variant="outlined" sx={{p: 2, borderRadius: 3, borderLeft: 3, borderLeftColor: "primary.main"}}>
                            <Stack gap={2}>
                                <Stack direction="row" alignItems="center" gap={1}>
                                    <Typography variant="h6">Step inicial del bot</Typography>
                                    <Tooltip title="Step donde iniciará el bot para los contactos que lleguen mediante esta plantilla.">
                                        <HelpOutline fontSize="small"/>
                                    </Tooltip>
                                </Stack>
                                <FormControl fullWidth>
                                    <InputLabel id="init-step-label">Seleccionar step</InputLabel>
                                    <Select
                                        labelId="init-step-label"
                                        label="Seleccionar step"
                                        value={initStep}
                                        onChange={(e) => setInitStep(e.target.value)}
                                        renderValue={(value) => {
                                            if (!value) return <em>Ninguno</em>;
                                            const step = botSteps.find((s) => (s.name || s) === value);
                                            return step
                                                ? (step.description || step.body?._text || step.name)
                                                : value;
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>Ninguno</em>
                                        </MenuItem>
                                        {botSteps.map((step) => {
                                            const stepName = step.name || step;
                                            const desc = step.description || step.body?._text || stepName;
                                            return (
                                                <MenuItem key={stepName} value={stepName}>
                                                    <Box>
                                                        <Typography fontWeight={600}>{stepName}</Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            maxWidth: 400
                                                        }}>
                                                            {desc}
                                                        </Typography>
                                                    </Box>
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            </Stack>
                        </Paper>

                        {/* Carousel Section */}
                        <CarouselSection
                            templateType={templateType}
                            headerMediaType={headerMediaType}
                            setHeaderMediaType={setHeaderMediaType}
                            numButtons={numButtons}
                            setNumButtons={setNumButtons}
                            buttonTypes={buttonTypes}
                            handleButtonTypeChange={handleButtonTypeChange}
                            carouselCards={carouselCards}
                            handleRemoveCard={handleRemoveCard}
                            handleFileUpload={handleFileUpload}
                            handleCardChange={handleCardChange}
                            addVariableToCard={addVariableToCard}
                            handleAddCard={handleAddCard}
                            DynamicButtonFields={DynamicButtonFields}
                        />

                        {/* Header */}
                        {category !== "authentication" && templateType !== "carousel" && (

                            <HeaderSection
                                headerType={headerType}
                                setHeaderType={setHeaderType}
                                headerText={headerText}
                                setHeaderText={setHeaderText}
                                headerTextVariable={headerTextVariable}
                                setHeaderTextVariable={setHeaderTextVariable}
                                handleFileChange={handleFileChange}
                                headerMedia={headerMedia}
                                headerMediaError={headerMediaError}
                                lat={lat}
                                setLat={setLat}
                                lng={lng}
                                setLng={setLng}
                                locName={locName}
                                setLocName={setLocName}
                                locAddress={locAddress}
                                setLocAddress={setLocAddress}
                                HEADER_LIMIT={HEADER_LIMIT}
                            />
                        )}

                        {/* Body */}
                        <BodySection
                            category={category}
                            body={body}
                            setBody={setBody}
                            bodyError={bodyError}
                            bodyVarIndices={bodyVarIndices}
                            bodySamples={bodySamples}
                            setBodySamples={setBodySamples}
                            BODY_LIMIT={BODY_LIMIT}
                            MAX_BODY_VARS={MAX_BODY_VARS}
                            wrapSelection={wrapSelection}
                            applyList={applyList}
                            bodyInputRef={bodyInputRef}
                            emojiAnchor={emojiAnchor}
                            setEmojiAnchor={setEmojiAnchor}
                            setEmojiPickerRef={setEmojiPickerRef}
                            handleAddBodyVariable={handleAddBodyVariable}
                        />
                        {/* Limited Time Offer Section */}
                        {templateType === "lto" && (
                            <Paper variant="outlined" sx={{p: 2, borderRadius: 3, borderLeft: 3, borderLeftColor: "primary.main"}}>
                                <Stack gap={2}>
                                    <Typography variant="h6">Oferta por tiempo limitado*</Typography>

                                    {/* Campo de título */}
                                    <TextField
                                        label="Título de la oferta"
                                        placeholder="Título de la oferta"
                                        value={ltoTitle}
                                        onChange={(e) => setLtoTitle(e.target.value.slice(0, 16))}
                                        helperText={`Caracteres: ${ltoTitle.length}/16`}
                                        fullWidth
                                        size="small"
                                        required
                                    />

                                    {/* Checkbox para periodo de expiración */}
                                    <Stack direction="row" alignItems="center" gap={1}>
                                        <Checkbox
                                            checked={ltoHasExpiry}
                                            onChange={(e) => setLtoHasExpiry(e.target.checked)}
                                        />
                                        <Typography variant="body2">
                                            Add expiry period to this offer{" "}
                                            <Tooltip title="Especifica si esta oferta tiene periodo de vencimiento.">
                                                <HelpOutline fontSize="small" color="action"/>
                                            </Tooltip>
                                        </Typography>
                                    </Stack>
                                </Stack>
                            </Paper>
                        )}

                        {/* OTP Body */}
                        {category === "authentication" && (
                            <OTPMessageContentSection
                                addSecurityRecommendation={addSecurityRecommendation}
                                setAddSecurityRecommendation={setAddSecurityRecommendation}
                                addExpirationTime={addExpirationTime}
                                setAddExpirationTime={setAddExpirationTime}
                                codeExpirationMinutes={codeExpirationMinutes}
                                setCodeExpirationMinutes={setCodeExpirationMinutes}
                            />
                        )}
                        {/* Buttons */}
                        {category !== "authentication" && templateType !== "carousel" && (
                            <ButtonsSection
                                quickReplies={quickReplies}
                                setQuickReplies={setQuickReplies}
                                ctas={ctas}
                                setCtas={setCtas}
                                totalButtons={totalButtons}
                                MAX_BUTTONS={MAX_BUTTONS}
                                urlCount={urlCount}
                                phoneCount={phoneCount}
                                MAX_URL_CTAS={MAX_URL_CTAS}
                                MAX_PHONE_CTAS={MAX_PHONE_CTAS}
                                urlIsValid={urlIsValid}
                                phoneIsValid={phoneIsValid}
                                openCtaMenu={openCtaMenu}
                                closeCtaMenu={closeCtaMenu}
                                ctaMenuEl={ctaMenuEl}
                            />
                        )}

                        {/* Footer + Actions */}
                        <FooterSection
                            category={category}
                            templateType={templateType}
                            footer={footer}
                            setFooter={setFooter}
                            remainingFooter={remainingFooter}
                        />

                        {submitError && (
                            <Typography variant="body2" color="error" sx={{mb: 1}}>
                                {submitError}
                            </Typography>
                        )}

                        <Stack direction={{xs: "column", sm: "row"}} gap={1}>
                            <Button variant="text" color="inherit" onClick={() => setClearAllOpen(true)}>
                                Limpiar todo
                            </Button>

                            <Button
                                variant="contained"
                                onClick={openPreviewModal}
                                disabled={
                                    // Caso AUTHENTICATION → solo requiere nombre válido
                                    category.toUpperCase() === "AUTHENTICATION"
                                        ? !!nameError
                                        : (
                                            nameError ||
                                            bodyError ||
                                            (headerType === "text" && !headerEnabled) ||
                                            (["image", "video", "document"].includes(headerType) &&
                                                (!!headerMediaError || !headerMedia)) ||
                                            (headerType === "location" && locationError) ||
                                            urlCount > MAX_URL_CTAS ||
                                            phoneCount > MAX_PHONE_CTAS ||
                                            ctas.some(
                                                (c) =>
                                                    c.type === "url" &&
                                                    c.urlType === "dynamic" &&
                                                    !c.sampleVar?.trim()
                                            )
                                        )
                                }
                            >
                                Vista previa y enviar
                            </Button>


                        </Stack>
                    </Stack>
                </Grid>

                {/* RIGHT: Preview */}
                <Grid item xs={12} md={5}>
                    <Box sx={{position: {md: "sticky"}, top: {md: 24}}}>
                        <PhonePreview
                            name={templateName}
                            headerType={headerType}
                            headerText={headerText}
                            headerTextVariable={headerTextVariable}
                            headerMedia={headerMediaPreview}
                            body={bodyPreview}
                            footer={footer}
                            quickReplies={quickReplies}
                            ctas={ctas}
                            category={category}
                            addSecurityRecommendation={addSecurityRecommendation}
                            addExpirationTime={addExpirationTime}
                            codeExpirationMinutes={codeExpirationMinutes}
                            templateType={templateType}
                            cards={mapCardsForPreview(carouselCards, headerMediaType || headerType, numButtons, buttonTypes)}
                        />

                    </Box>
                </Grid>
            </Grid>
            <PreviewModal
                open={openPreview}
                onClose={closePreviewModal}
                onSubmit={handleSubmit}
                loading={submitting}
                summary={{
                    name: templateName || "—",
                    category,
                    language,
                    headerTypeLabel,
                    templateBody: body,
                    exampleBody: bodyPreview,
                }}
                previewProps={{
                    name: templateName,
                    headerType: headerType,
                    headerText: headerText,
                    headerTextVariable: headerTextVariable,
                    headerMedia: headerMediaPreview,
                    body: bodyPreview,
                    footer: footer,
                    quickReplies: quickReplies,
                    ctas: ctas,
                    category: category,
                    addSecurityRecommendation: addSecurityRecommendation,
                    addExpirationTime: addExpirationTime,
                    codeExpirationMinutes: codeExpirationMinutes,
                    templateType: templateType,
                    cards: mapCardsForPreview(carouselCards, headerMediaType || headerType, numButtons, buttonTypes),
                    submitError: submitError,
                }}
            />
            {/* 🔔 Diálogo de confirmación para limpiar */}
            <Dialog open={clearAllOpen} onClose={() => setClearAllOpen(false)}>
                <DialogTitle>¿Limpiar formulario?</DialogTitle>
                <DialogContent>
                    <Typography>Se perderán todos los datos ingresados. ¿Continuar?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setClearAllOpen(false)} color="inherit">
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => {
                            handleClearAll();
                            setClearAllOpen(false);
                        }}
                        variant="contained"
                        color="error"
                    >
                        Limpiar
                    </Button>
                </DialogActions>
            </Dialog>
            {/* 🔔 Diálogo de confirmación */}
            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
                <DialogTitle>✅ Template creado</DialogTitle>
                <DialogContent>
                    <Typography>¿Deseas crear otro template?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            handleClearAll();
                            setOpenConfirm(false);
                        }}
                        variant="contained"
                    >
                        Sí
                    </Button>
                    <Button
                        onClick={() => {
                            setOpenConfirm(false);
                            if (onSuccess) onSuccess();
                            else navigate("/admin/templates");
                        }}
                        color="inherit"
                    >
                        No
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}